import React, { useMemo, useState, useEffect } from 'react'
import {
  GoogleMap,
  Marker,
  Polyline,
  useJsApiLoader,
  InfoWindow
} from '@react-google-maps/api'
import RenderDataModal from '../RenderViewModal/RenderViewModal'
import './LocationsModal.css'
import dayjs from 'dayjs'
import utc from 'dayjs/plugin/utc'
dayjs.extend(utc)

// Define which Google Maps libraries to load — `geometry` is needed for heading calculations
type GoogleMapsLibrary = 'places' | 'geometry' | 'drawing' | 'visualization'
const googleMapLibraries: GoogleMapsLibrary[] = ['geometry']

// Define the map container dimensions
const containerStyle = {
  width: '100%',
  height: '500px'
}

// Type definition for a single location point
export type LocationPoint = {
  id: number
  latitude: number
  longitude: number
  gpsTimestamp: string
  speedMph: number
  accuracy: number
}

type Props = {
  isOpen: boolean
  onClose: () => void
  vehicleName: string
  locations: LocationPoint[]
  googleMapsApiKey: string
}

const LocationMapModal: React.FC<Props> = ({
  isOpen,
  onClose,
  vehicleName,
  locations,
  googleMapsApiKey
}) => {
  // Load Google Maps with desired libraries
  const { isLoaded } = useJsApiLoader({
    googleMapsApiKey,
    libraries: googleMapLibraries
  })

  // UI state controls
  const [showRoute, setShowRoute] = useState(false)
  const [animate, setAnimate] = useState(false)
  const [animatedMarkerIndex, setAnimatedMarkerIndex] = useState(0)
  const [showMarkers, setShowMarkers] = useState(true)
  const [selectedMarker, setSelectedMarker] = useState<LocationPoint | null>(
    null
  )

  // Compute initial map center based on first location
  const center = useMemo(() => {
    if (locations.length > 0) {
      return {
        lat: locations[0].latitude,
        lng: locations[0].longitude
      }
    }
    return { lat: 0, lng: 0 }
  }, [locations])

  const animationIcon = useMemo(
    () =>
      _getAnimationIcon(
        locations[animatedMarkerIndex],
        locations[animatedMarkerIndex + 1]
      ),
    [animatedMarkerIndex, locations]
  )

  // Animate marker along path with a timed interval
  useEffect(() => {
    if (!animate || locations.length < 2) return

    setShowMarkers(false) // Hide static markers while animating

    const interval = setInterval(() => {
      setAnimatedMarkerIndex((prev) => {
        // If reached end of path, stop animation and reset marker
        if (prev >= locations.length - 2) {
          clearInterval(interval)
          setAnimate(false)
          setAnimatedMarkerIndex(0)
          setShowMarkers(true) // Show markers again
          return prev
        }
        return prev + 1
      })
    }, 300)

    return () => clearInterval(interval)
  }, [animate, locations])

  if (!isOpen) return null // Do not render map if modal is closed

  // Format the modal title with timestamp range
  const title = `${vehicleName}${
    locations.length > 0
      ? ` (${dayjs(locations[0].gpsTimestamp)
          .local()
          .format('YYYY-MM-DD HH:mm:ss')} - ${dayjs(
          locations[locations.length - 1].gpsTimestamp
        )
          .local()
          .format('YYYY-MM-DD HH:mm:ss')})`
      : ''
  }`

  return (
    <RenderDataModal onClose={onClose} title={title}>
      {!isLoaded ? (
        <p>Loading Map...</p>
      ) : locations.length === 0 ? (
        <div className="dx-empty-message">No results found</div>
      ) : (
        <>
          {/* Map control buttons */}
          <div className="location-controls">
            <button onClick={() => setShowMarkers(!showMarkers)}>
              {showMarkers ? 'Hide Markers' : 'Show Markers'}
            </button>
            <button onClick={() => setShowRoute(!showRoute)}>
              {showRoute ? 'Hide Route' : 'Show Route'}
            </button>
            <button
              onClick={() => {
                if (!animate) setAnimatedMarkerIndex(0)
                setAnimate(!animate)
              }}
            >
              {animate ? 'Stop' : 'Animate'}
            </button>
          </div>

          {/* Google Map */}
          <GoogleMap
            mapContainerStyle={containerStyle}
            center={center}
            zoom={16}
          >
            {/* Static location markers (disabled during animation) */}
            {showMarkers &&
              locations.map((loc, idx) => (
                <Marker
                  key={idx}
                  position={{ lat: loc.latitude, lng: loc.longitude }}
                  onClick={() => setSelectedMarker(loc)}
                />
              ))}

            {/* InfoWindow */}
            {selectedMarker && (
              <InfoWindow
                position={{
                  lat: selectedMarker.latitude,
                  lng: selectedMarker.longitude
                }}
                onCloseClick={() => setSelectedMarker(null)}
              >
                <div>
                  <div>
                    <strong>
                      {dayjs(selectedMarker.gpsTimestamp)
                        .local()
                        .format('YYYY-MM-DD HH:mm:ss')}
                    </strong>
                  </div>
                  <div>
                    ({selectedMarker.latitude.toFixed(6)},{' '}
                    {selectedMarker.longitude.toFixed(6)})
                  </div>
                  <div>Speed: {selectedMarker.speedMph.toFixed(2)} MPH</div>
                  <div>Accuracy: {selectedMarker.accuracy.toFixed(3)}m</div>
                </div>
              </InfoWindow>
            )}

            {/* Route polyline */}
            {showRoute && (
              <Polyline
                path={locations.map((loc) => ({
                  lat: loc.latitude,
                  lng: loc.longitude
                }))}
                options={{
                  strokeColor: '#4285F4',
                  strokeOpacity: 0.8,
                  strokeWeight: 2
                }}
              />
            )}

            {/* Animated marker */}
            {animate && animationIcon && (
              <Marker
                position={{
                  lat: locations[animatedMarkerIndex].latitude,
                  lng: locations[animatedMarkerIndex].longitude
                }}
                icon={animationIcon}
              />
            )}
          </GoogleMap>
        </>
      )}
    </RenderDataModal>
  )
}

export default LocationMapModal

const _getAnimationIcon = (
  current: LocationPoint,
  next: LocationPoint | undefined
) => {
  if (
    !next ||
    typeof window === 'undefined' ||
    !window.google?.maps?.geometry
  ) {
    return null
  }

  const heading = window.google.maps.geometry.spherical.computeHeading(
    new window.google.maps.LatLng(current.latitude, current.longitude),
    new window.google.maps.LatLng(next.latitude, next.longitude)
  )

  return {
    path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
    scale: 5,
    strokeWeight: 2,
    fillColor: '#0F0',
    fillOpacity: 1,
    rotation: heading
  }
}
